import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { posApi, authApi } from '@/services/api';
import { X, FileText, ArrowRightCircle, Eye, Download, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatMoney } from '@/utils/currency';
import { useNavigate } from 'react-router-dom';
import { useCartStore } from '@/store/cartStore';
import Pagination from '@/components/common/Pagination';
import { isLocalFirstMode } from '@/offline';
import { useAuthStore } from '@/store/authStore';
import { isCompanyAdmin } from '@/types';
import { downloadQuotationPdf } from '@/utils/quotationExportPdf';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  customerName: string;
}

export default function CustomerQuotationsModal({ isOpen, onClose, customerId, customerName }: Props) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const cart = useCartStore();
  const [page, setPage] = useState(1);
  const limit = 10;
  
  const role = useAuthStore((s) => s.role) || '';
  const isAdmin = isCompanyAdmin(role);

  const [detailsModalId, setDetailsModalId] = useState<string | null>(null);
  const [deleteModalId, setDeleteModalId] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (isOpen && customerId) {
      qc.invalidateQueries({ queryKey: ['quotations', customerId] });
    }
  }, [isOpen, customerId, qc]);

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const res = await authApi.getMyProfile();
      return res.data?.message || res.data;
    },
  });

  const { data: qData, isLoading } = useQuery({
    queryKey: ['quotations', customerId, page],
    queryFn: async () => {
      let localQs: any[] = [];
      if (isLocalFirstMode()) {
        const { companyDb } = await import('@/offline');
        const allLocal = await companyDb().local_quotations.toArray();
        localQs = allLocal.filter((q: any) => q.customer === customerId || q.customerName === customerId);
      }
      try {
        const res = await posApi.getQuotations({ customer: customerId, page, limit });
        const serverQs = res.data?.message?.data || res.data?.data || [];
        const totalPages = res.data?.message?.total_pages || res.data?.total_pages || 1;
        
        // Merge localQs and serverQs (avoid duplicates by id)
        const serverIds = new Set(serverQs.map((q: any) => q.name));
        const missingLocal = localQs.filter(q => !serverIds.has(q.name) && !serverIds.has(q.client_offline_id));
        
        return { data: [...missingLocal, ...serverQs], total_pages: totalPages };
      } catch (err) {
        if (isLocalFirstMode()) {
          return { data: localQs, total_pages: 1 };
        }
        throw err;
      }
    },
    enabled: isOpen && !!customerId,
  });

  const quotations = qData?.data || [];
  const totalPages = qData?.total_pages || 1;

  const { data: quotationDetails, isLoading: isDetailsLoading } = useQuery({
    queryKey: ['quotation', detailsModalId],
    queryFn: async () => {
      if (!detailsModalId) return null;
      if (isLocalFirstMode()) {
        const { companyDb } = await import('@/offline');
        const localQ = await companyDb().local_quotations.get(detailsModalId);
        if (localQ) return localQ;
      }
      const res = await posApi.getQuotation(detailsModalId);
      return res.data?.message || res.data;
    },
    enabled: !!detailsModalId,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (isLocalFirstMode()) {
        const { companyDb } = await import('@/offline');
        const localQ = await companyDb().local_quotations.get(id);
        if (localQ) {
          await companyDb().local_quotations.delete(id);
          if (localQ.client_offline_id) {
             const pending = await companyDb().sync_queue.where('clientOfflineId').equals(localQ.client_offline_id).toArray();
             for (const p of pending) {
               await companyDb().sync_queue.delete(p.clientOfflineId);
             }
          }
          return;
        }
      }
      await posApi.deleteQuotation(id);
    },
    onSuccess: () => {
      toast.success('Quotation deleted successfully');
      qc.invalidateQueries({ queryKey: ['quotations'] });
      setDeleteModalId(null);
    },
    onError: () => toast.error('Failed to delete quotation'),
  });

  const convertMutation = useMutation({
    mutationFn: async (id: string) => {
      let quotation: any = null;
      if (isLocalFirstMode()) {
        const { companyDb } = await import('@/offline');
        quotation = await companyDb().local_quotations.get(id);
      }
      
      if (!quotation) {
        const res = await posApi.getQuotation(id);
        quotation = res.data?.message || res.data;
        await posApi.convertQuotation(id);
      } else {
        // mark local as converted
        const { companyDb } = await import('@/offline');
        await companyDb().local_quotations.update(id, { status: 'Converted' });
      }
      
      const { companyDb } = await import('@/offline');
      if (quotation.items && quotation.items.length > 0) {
        for (let i = 0; i < quotation.items.length; i++) {
           const dbItem = await companyDb().catalog_items.get(quotation.items[i].item);
           if (dbItem) {
             quotation.items[i].track_serials = !!(dbItem as any).track_serials;
           }
        }
      }
      
      return quotation;
    },
    onSuccess: (quotation) => {
      toast.success('Quotation loaded into POS');
      qc.invalidateQueries({ queryKey: ['quotations'] });
      
      cart.clearCart();
      if (quotation.customer) {
        cart.setCustomer(quotation.customer, quotation.customer_name || quotation.customer);
      }
      cart.setDiscount(quotation.discount_amount || 0);
      cart.setTax(quotation.tax_amount || 0);
      
      const items = quotation.items || [];
      items.forEach((item: any) => {
        cart.addItem({
          item: item.item,
          item_name: item.item_name,
          quantity: item.qty || item.quantity || 1,
          rate: item.rate,
          amount: item.amount || item.rate,
          uom: item.uom,
          item_type: 'Product',
          track_serials: !!item.track_serials,
        } as any);
      });
      
      onClose();
      navigate('/');
    },
    onError: () => toast.error('Failed to convert quotation'),
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100 bg-gray-50/50">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              Quotations for {customerName || customerId}
            </h2>
            <p className="text-sm text-gray-500 mt-1">View and convert pending quotations to sales</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                cart.clearCart();
                cart.setCustomer(customerId, customerName || customerId);
                onClose();
                navigate('/', { state: { isQuotationMode: true } });
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
            >
              <FileText className="w-4 h-4" />
              Add Quotation
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 sm:p-6">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" />
            </div>
          ) : quotations.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-900">No Quotations Found</h3>
              <p className="text-gray-500 mt-1">This customer does not have any pending quotations.</p>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 text-gray-600 font-medium border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3">Quotation ID</th>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Items</th>
                      <th className="px-4 py-3">Total Amount</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {quotations.map((q: any) => (
                      <tr key={q.name} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-900">{q.name}</td>
                        <td className="px-4 py-3 text-gray-500">
                          {new Date(q.creation || q.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-gray-500">
                          {q.total_qty || q.items?.length || 0} items
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {formatMoney(q.grand_total || q.net_total || 0)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setDetailsModalId(q.name)}
                              className="btn-secondary text-xs py-1.5 px-3 flex items-center justify-center gap-1 w-24"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" /> Details
                            </button>
                            <button
                              onClick={() => convertMutation.mutate(q.name)}
                              disabled={convertMutation.isPending}
                              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-medium rounded-lg transition-colors text-xs w-28 disabled:opacity-50"
                            >
                              <ArrowRightCircle className="w-4 h-4" />
                              Proceed to Sale
                            </button>
                            {isAdmin && (
                              <button
                                onClick={() => setDeleteModalId(q.name)}
                                className="text-red-600 hover:text-red-700 p-1.5 hover:bg-red-50 rounded w-8 flex justify-center"
                                title="Delete Quotation"
                                disabled={deleteMutation.isPending}
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer with Pagination */}
        {!isLoading && totalPages > 1 && (
          <div className="p-4 border-t border-gray-100 bg-white">
            <Pagination
              page={page - 1} // Pagination component is 0-indexed
              pageSize={limit}
              total={totalPages * limit}
              onPageChange={(newPage) => setPage(newPage + 1)}
            />
          </div>
        )}
      </div>

      {detailsModalId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">
                Quotation Details: {detailsModalId}
              </h3>
              <div className="flex items-center gap-2">
                {quotationDetails && (
                  <button
                    onClick={async () => {
                      try {
                        setIsDownloading(true);
                        await downloadQuotationPdf(quotationDetails, profile);
                        toast.success('PDF Downloaded successfully');
                      } catch (err) {
                        toast.error('Failed to generate PDF');
                        console.error(err);
                      } finally {
                        setIsDownloading(false);
                      }
                    }}
                    disabled={isDownloading}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Download PDF
                  </button>
                )}
                <button
                  onClick={() => setDetailsModalId(null)}
                  className="p-1 text-gray-400 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {isDetailsLoading ? (
                <div className="py-12 text-center text-gray-500">Loading details...</div>
              ) : quotationDetails ? (
                <div>
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div>
                      <p className="text-sm text-gray-500">Customer</p>
                      <p className="font-medium text-gray-900">{quotationDetails.customer_name || quotationDetails.customer}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Date</p>
                      <p className="font-medium text-gray-900">{new Date(quotationDetails.creation || quotationDetails.createdAt).toLocaleString()}</p>
                    </div>
                    {quotationDetails.valid_till && (
                      <div>
                        <p className="text-sm text-gray-500">Expiry Date</p>
                        <p className="font-medium text-gray-900">{new Date(quotationDetails.valid_till).toLocaleDateString()}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-gray-500">Total Amount</p>
                      <p className="font-medium text-gray-900">{formatMoney(quotationDetails.net_total || quotationDetails.grand_total || 0)}</p>
                    </div>
                  </div>

                  <h4 className="font-medium text-gray-900 mb-3">Items</h4>
                  <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-gray-100 border-b border-gray-200 text-gray-600">
                        <tr>
                          <th className="px-4 py-2 font-medium">Item</th>
                          <th className="px-4 py-2 font-medium text-right">Qty</th>
                          <th className="px-4 py-2 font-medium text-right">Rate</th>
                          <th className="px-4 py-2 font-medium text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {(quotationDetails.items || []).map((item: any, idx: number) => (
                          <tr key={idx} className="bg-white">
                            <td className="px-4 py-2">
                              <div className="font-medium text-gray-900">{item.item_name}</div>
                              <div className="text-xs text-gray-500">{item.item}</div>
                            </td>
                            <td className="px-4 py-2 text-right">{item.qty || item.quantity} {item.uom}</td>
                            <td className="px-4 py-2 text-right">{formatMoney(item.rate)}</td>
                            <td className="px-4 py-2 text-right font-medium">{formatMoney(item.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="py-12 text-center text-gray-500">Failed to load details.</div>
              )}
            </div>
            <div className="p-4 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setDetailsModalId(null)}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteModalId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden flex flex-col">
            <div className="p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <X className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Quotation</h3>
              <p className="text-gray-500 text-sm">
                Are you sure you want to delete quotation <span className="font-semibold text-gray-700">{deleteModalId}</span>? This action cannot be undone.
              </p>
            </div>
            <div className="p-4 bg-gray-50 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => setDeleteModalId(null)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={deleteMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteModalId)}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
